import { useCallback, useRef } from "react";

import { EmailCallContextPayloadI } from "shared/lib/interfaces/email/ai";

/**
 * What the rep currently has on screen, as opposed to what the server has.
 *
 * Both the prompt and the call context persist on their own schedule - the
 * prompt on a 3 second throttle, the call context on an explicit Save click -
 * and Compose used to generate from whatever the server happened to hold.
 * Inside those windows that is the previous text, which is what "it used one
 * of the old prompts" and "it ignored the transcript" both look like from the
 * rep's side.
 *
 * Held in refs, not state. Nothing renders these values; they are read once,
 * inside handleComposeWithAI, at the moment the rep clicks. As state they sat
 * in the sidebar's context, so every keystroke in the prompt box or the
 * transcript box re-rendered every consumer of that context - including the
 * Tiptap editor and the follow-up preview - on the typing hot path, with a
 * 25,000 character transcript in the room.
 *
 * Keyed by id rather than held as a single current value, because the sidebar
 * stays mounted across template and contact switches. A single slot would let
 * one contact's transcript be sent for another.
 */
export interface ComposeDraftsI {
  /** Record the prompt the rep has typed for this template. */
  setPromptDraft: (templateId: string, prompt: string) => void;
  /**
   * Forget any local edit, so the server's copy is authoritative again.
   * Called when a template is (re)hydrated from the server.
   */
  clearPromptDraft: (templateId: string) => void;
  getPromptDraft: (templateId?: string) => string | undefined;

  /** Record the call context on screen for this contact, saved or not. */
  setCallContextDraft: (
    contactId: string,
    callContext: EmailCallContextPayloadI
  ) => void;
  getCallContextDraft: (
    contactId?: string
  ) => EmailCallContextPayloadI | undefined;
}

export const useComposeDrafts = (): ComposeDraftsI => {
  const promptDrafts = useRef<Record<string, string>>({});
  const callContextDrafts = useRef<Record<string, EmailCallContextPayloadI>>(
    {}
  );

  const setPromptDraft = useCallback((templateId: string, prompt: string) => {
    if (templateId) {
      promptDrafts.current[templateId] = prompt;
    }
  }, []);

  const clearPromptDraft = useCallback((templateId: string) => {
    if (templateId) {
      delete promptDrafts.current[templateId];
    }
  }, []);

  const getPromptDraft = useCallback(
    (templateId?: string) =>
      templateId ? promptDrafts.current[templateId] : undefined,
    []
  );

  const setCallContextDraft = useCallback(
    (contactId: string, callContext: EmailCallContextPayloadI) => {
      if (contactId) {
        callContextDrafts.current[contactId] = callContext;
      }
    },
    []
  );

  const getCallContextDraft = useCallback(
    (contactId?: string) =>
      contactId ? callContextDrafts.current[contactId] : undefined,
    []
  );

  return {
    setPromptDraft,
    clearPromptDraft,
    getPromptDraft,
    setCallContextDraft,
    getCallContextDraft,
  };
};
